import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Ticket, UserCheck, Loader2 } from "lucide-react";
import {
  hidratarComprasBoletos,
  obtenerCompraBackend,
} from "@/data/comprasStore";
import { completarTurno } from "@/data/smartQueueStore";
import { getCurrentSocio } from "@/data/socioAuthStore";
import { COMPRA_CONFIRMADA } from "@/constants/testIds";

/**
 * Confirmación de compra · Sprint 54 · Fase A (ruta estable).
 *
 * Ruta: `/app/compra-confirmada/:compraId`. La compra se recupera SIEMPRE
 * desde el backend por su id estable (fuente de verdad, con autorización
 * de propiedad: un socio no puede ver la compra de otro → 403 → redirige).
 * `history.state` se usa solo como optimización para pintar de inmediato.
 *
 * Funciona con navegación normal, F5, pestaña nueva, copiar/pegar URL,
 * cache vacío, respuesta lenta y botón Atrás. No crea compras ni pagos:
 * recargar o retroceder nunca duplica nada.
 */
const fmtMXN = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
    : n;

export default function CompraConfirmada() {
  const { compraId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const socio = getCurrentSocio();

  // Optimización: si venimos del flujo de pago, `state.compra` puede traer
  // la compra ya cargada para pintar sin esperar al backend.
  const compraOptimista =
    location.state && location.state.compra && location.state.compra.id === compraId
      ? location.state.compra
      : null;

  const [compra, setCompra] = useState(compraOptimista);
  const [estado, setEstado] = useState(compraOptimista ? "ok" : "cargando");
  const [terminando, setTerminando] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!compraId) {
        setEstado("no-encontrada");
        return;
      }
      // Fuente de verdad: backend (aplica autorización de propiedad).
      const c = await obtenerCompraBackend(compraId);
      if (cancelled) return;
      if (c && c.id && c.estado === "CONFIRMADA") {
        setCompra(c);
        setEstado("ok");
        hidratarComprasBoletos().catch(() => {});
      } else {
        // 403/404/estado no confirmado → no mostramos confirmación ajena.
        setEstado("no-encontrada");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [compraId]);

  if (!socio) return <Navigate to="/login" replace />;
  if (estado === "no-encontrada")
    return <Navigate to="/app/mis-boletos" replace />;

  if (estado === "cargando" && !compra) {
    return (
      <div
        data-testid={COMPRA_CONFIRMADA.cargando}
        className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center"
      >
        <div className="w-14 h-14 grid place-items-center rounded-2xl bg-accent/15 text-accent">
          <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.6} />
        </div>
        <p className="text-sm text-muted-foreground">
          Recuperando tu compra…
        </p>
      </div>
    );
  }

  const c = compra;
  const esSandbox = !!c.simulado;

  // "Comprar más boletos" · el turno de Smart Queue SIGUE ACTIVO.
  const comprarMas = () => {
    navigate(`/app/eventos/${c.eventoId}/seleccion-mesa`);
  };

  // "Terminar y asignar asistentes" · cede el turno y navega a la asignación.
  const terminarYAsignar = async () => {
    if (terminando) return;
    setTerminando(true);
    const res = await completarTurno(c.eventoId);
    if (res && res.error) {
      setTerminando(false);
      toast.error("No pudimos finalizar tu turno. Intenta de nuevo.");
      return;
    }
    navigate(`/app/asignar/${c.id}`);
  };

  return (
    <div
      data-testid={COMPRA_CONFIRMADA.screen}
      className="min-h-[60vh] flex items-center justify-center"
    >
      <div className="w-full max-w-lg rounded-2xl border hairline bg-card p-8 md:p-10 text-center shadow-soft animate-fade-up">
        <div className="mx-auto w-14 h-14 grid place-items-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/40 text-emerald-600 dark:text-emerald-400 mb-6">
          <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
        </div>
        <span className="overline text-accent">Confirmado</span>
        <h1 className="mt-3 font-heading text-2xl sm:text-3xl text-foreground">
          Compra confirmada
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Recibimos tu pago correctamente. Tus boletos ya están disponibles en
          &quot;Mis Boletos&quot;.
        </p>

        {esSandbox && (
          <div
            data-testid={COMPRA_CONFIRMADA.sandbox}
            className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-300"
          >
            Operación de prueba — Ambiente sandbox
          </div>
        )}

        <div
          data-testid={COMPRA_CONFIRMADA.recap}
          className="mt-6 rounded-xl border hairline bg-background/60 p-4 text-left text-sm text-muted-foreground space-y-1.5"
        >
          <p>
            <span className="text-foreground font-medium">Evento:</span>{" "}
            {c.nombreEvento}
          </p>
          <p>
            <span className="text-foreground font-medium">Mesa:</span> Mesa{" "}
            {c.mesa}
          </p>
          <p>
            <span className="text-foreground font-medium">Boletos:</span>{" "}
            {c.cantidad} {c.cantidad === 1 ? "boleto" : "boletos"}
          </p>
          <p>
            <span className="text-foreground font-medium">Total pagado:</span>{" "}
            <span data-testid={COMPRA_CONFIRMADA.total}>
              {fmtMXN(Number(c.total))}
            </span>
          </p>
          <p>
            <span className="text-foreground font-medium">Referencia:</span>{" "}
            <span data-testid={COMPRA_CONFIRMADA.referencia}>
              {c.referencia || c.pagoId || "—"}
            </span>
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            data-testid={COMPRA_CONFIRMADA.btnComprarMas}
            onClick={comprarMas}
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
          >
            <Ticket className="w-4 h-4" strokeWidth={1.7} />
            Comprar más boletos
          </button>
          <button
            type="button"
            data-testid={COMPRA_CONFIRMADA.btnTerminarAsignar}
            onClick={terminarYAsignar}
            disabled={terminando}
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <UserCheck className="w-4 h-4" strokeWidth={1.7} />
            {terminando ? "Finalizando…" : "Terminar y asignar asistentes"}
          </button>
        </div>
      </div>
    </div>
  );
}
